//#region imports
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { selectAllAirportLocations } from "_redux/slices/timeline/timeline-areas";

// redux
import { useSelector } from "react-redux";
import { selectEmployeeBeaconById } from '_redux/slices/timeline/employee-beacons';
import { selectEmployeeLocationsById } from '_redux/slices/timeline/live-location-tab';

// styling
import { Colors } from '_styles';
import { IS_PHONE_TIMELINE } from '../timeline-constants';
import { FIREBASE_TARGET } from '_utils/firebase-projects';

// SGS doesn't run staff location tracking (beacons / GPS), so every badge would read "Unknown"
const SHOW_EMPLOYEE_LOCATION = FIREBASE_TARGET !== 'sgs';

// spacing
import { Spacing } from '_styles';
import { EMPLOYEE_STATUS_META, timelineRoles } from '_utils/timeline-constants';
import { FaUser, FaShuttleVan, FaMapMarkerAlt, FaCheck, FaMinus, FaTimes, FaCoffee } from 'react-icons/fa';

//#endregion

const MAX_GEOFENCE_DISTANCE = 20;
const BEACON_PRIORITY_WINDOW_MS = 120_000; // 120 s — beacon within this window wins even if GPS is newer



const EmployeeProfilePicture = (props) => {
    const avatarSize = IS_PHONE_TIMELINE ? 30 : 40;

    //#region states
    const [shiftStart, setShiftStart] = useState("");
    const [shiftEnd, setShiftEnd] = useState("");
    const [employeeIcon, setEmployeeIcon] = useState("");
    const [employeeRole, setEmployeeRole] = useState("");
    const [employeeEquipment, setEmployeeEquipment] = useState(false);
    const [infoTooltip, setInfoTooltip] = useState(null);
    const infoTooltipTimer = useRef(null);
    const latestValidLocationRef = useRef(null);
    const employeeId = props?.employee?.employeeId;
    //#endregion

    //#region redux
    const airportLocations = useSelector(selectAllAirportLocations);
    const empBeaconById = useSelector((state) => selectEmployeeBeaconById(state, props?.employee?.employeeId));
    const empLocationById = useSelector((state) => selectEmployeeLocationsById(state, props?.employee?.employeeId));
    //#endregion

    //#region helpers

    const employeeStatus = props?.employee?.status || props?.employeeStatus || "offline";
    const statusMeta = EMPLOYEE_STATUS_META[employeeStatus] || EMPLOYEE_STATUS_META.offline;

    // is inside haversine for circular
    const haversineMeters = (lat1, lon1, lat2, lon2) => {
        const R = 6371000;
        const toRad = (d) => (d * Math.PI) / 180;

        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);

        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

        return 2 * R * Math.asin(Math.sqrt(a));
    };

    // is inside polygon helper
    const pointInPolygon = (lat, lon, polygon) => {
        if (!Array.isArray(polygon) || polygon.length < 3) return false;

        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const [latI, lonI] = polygon[i];
            const [latJ, lonJ] = polygon[j];

            const intersects =
                (lonI > lon) !== (lonJ > lon) &&
                lat < ((latJ - latI) * (lon - lonI)) / (lonJ - lonI + 0.0) + latI;

            if (intersects) inside = !inside;
        }
        return inside;
    };

    // polygon bbox helper: create small bounding box around polygon to determine if nearest
    const bboxFromPolygon = (polygon) => {
        let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
        for (const [la, lo] of polygon) {
            const lat = Number(la);
            const lon = Number(lo);
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
            minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
            minLon = Math.min(minLon, lon); maxLon = Math.max(maxLon, lon);
        }
        return { minLat, maxLat, minLon, maxLon };
    };

    /**
     * pre-compute locations
     * - This is because polygon check is expensive, hence plot boundingbox around each polygon based location
     * - This creates minimum and maximum lat and lon for each polygons, helping us to find if user is near
     */
    const geoIndex = useMemo(() => {
        if (!Array.isArray(airportLocations)) return [];

        return airportLocations
            .map((loc) => {
                const g = loc?.geofenceDetails;

                // return if no geofence
                if (!g?.type) return null;

                if (g.type === "circular") {
                    const c = g.circular || {};
                    const lat = Number(c.lat);
                    const lon = Number(c.long);
                    const radius = Number(c.radius);

                    if (!Number.isFinite(lat) || !Number.isFinite(lon) || !Number.isFinite(radius)) return null;

                    return { type: "circular", loc, lat, lon, radius };
                }

                if (g.type === "polygon") {
                    const pts = (g.polygon || [])
                        .map(([la, lo]) => [Number(la), Number(lo)])
                        .filter(([la, lo]) => Number.isFinite(la) && Number.isFinite(lo));

                    if (pts.length < 3) return null;
                    return { type: "polygon", loc, pts, bbox: bboxFromPolygon(pts) };
                }

                return null;
            })
            .filter(Boolean);
    }, [airportLocations]);

    // toEPOCH
    const toEpochMs = (ts) => {
        if (!ts) return NaN;

        // Raw Unix ms number (backend now stores latestBeaconTime as a number)
        if (typeof ts === "number") {
            return Number.isFinite(ts) ? ts : NaN;
        }

        // ISO
        if (typeof ts === "string" && ts.includes("T") && ts.endsWith("Z")) {
            const ms = Date.parse(ts);
            return Number.isFinite(ms) ? ms : NaN;
        }

        // regular string
        if (typeof ts === "string") {
            const normalized = ts.replace(" at ", " ");
            const ms = Date.parse(normalized);
            return Number.isFinite(ms) ? ms : NaN;
        }

        return NaN;
    };

    const timeAgoFromMs = (ms) => {
        if (!Number.isFinite(ms)) return null;
        const mins = Math.max(0, Math.floor((Date.now() - ms) / 60000));
        if (mins === 0) return "Just now";
        if (mins === 1) return "1 min ago";
        if (mins < 60) return `${mins} min ago`;
        const hrs = Math.floor(mins / 60);
        return hrs === 1 ? "1 hr ago" : `${hrs} hrs ago`;
    };

    const compactTimeAgo = (ms) => {
        if (!Number.isFinite(ms)) return null;
        const mins = Math.max(0, Math.floor((Date.now() - ms) / 60000));
        if (mins === 0) return "Now";
        if (mins < 60) return `${mins} mins`;
        return "1h+";
    };

    // determine source
    const latestSource = ({ beaconTime, geoTime }) => {

        const beaconMs = toEpochMs(beaconTime);
        const geoMs = toEpochMs(geoTime);

        // return if none exists
        if (!Number.isFinite(beaconMs) && !Number.isFinite(geoMs)) {
            return { source: null, latestMs: NaN };
        }

        // return if geoloc time is invalid
        if (!Number.isFinite(geoMs)) return { source: "beacon", latestMs: beaconMs };

        // return if beacon time is invalid
        if (!Number.isFinite(beaconMs)) return { source: "geo", latestMs: geoMs };

        // compare
        return beaconMs >= geoMs
            ? { source: "beacon", latestMs: beaconMs }
            : { source: "geo", latestMs: geoMs };
    };

    // find loc based on beacon
    const findBeaconLoc = (latestBeacon, beaconTimeStr) => {
        const major = Number(latestBeacon?.major);
        const minor = Number(latestBeacon?.minor);
        if (!Number.isFinite(major) || !Number.isFinite(minor)) return null;

        const loc = airportLocations.find(
            (loc) =>
                Array.isArray(loc.beaconDetails) &&
                loc.beaconDetails.some(
                    (b) => Number(b.major) === major && Number(b.minor) === minor
                )
        );
        if (!loc) return null;

        return {
            title: loc.title,
            latestTimeAgo: timeAgoFromMs(toEpochMs(beaconTimeStr)),
            latestEpochMs: toEpochMs(beaconTimeStr),
            latestBeaconTime: beaconTimeStr,
            source: "beacon",
        };
    };

    // find loc based on geolocation
    const findGeoLoc = (latestGeo, geoTimeStr) => {
        const lat = Number(latestGeo?.latitude);
        const lon = Number(latestGeo?.longitude);

        // early return if not valid lat || lon
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

        let bestCircle = null;

        // for each location check if user is inside or not
        for (const item of geoIndex) {

            // Polygon check — return immediately on first match
            if (item.type === "polygon") {
                const { bbox, pts } = item;

                // bbox reject
                if (lat < bbox.minLat || lat > bbox.maxLat || lon < bbox.minLon || lon > bbox.maxLon) continue;

                if (pointInPolygon(lat, lon, pts)) {
                    return {
                        title: item.loc.title,
                        latestTimeAgo: timeAgoFromMs(toEpochMs(geoTimeStr)),
                        latestEpochMs: toEpochMs(geoTimeStr),
                        latestGeoTime: geoTimeStr,
                        source: "geo",
                    };
                }
                continue;
            }

            // Circular — keep nearest within MAX_GEOFENCE_DISTANCE
            if (item.type === "circular") {
                const d = haversineMeters(lat, lon, item.lat, item.lon);
                const edgeDist = Math.max(0, d - item.radius);

                if (edgeDist > MAX_GEOFENCE_DISTANCE) continue;

                if (!bestCircle || edgeDist < bestCircle.edgeDist) {
                    bestCircle = { loc: item.loc, edgeDist };
                }
            }
        }

        if (!bestCircle) return null;

        return {
            title: bestCircle.loc.title,
            source: "geo",
            latestGeoTime: geoTimeStr,
            latestTimeAgo: timeAgoFromMs(toEpochMs(geoTimeStr)),
            latestEpochMs: toEpochMs(geoTimeStr),
        };
    };

    //#endregion

    //#region use effects

    // if employeeId changes when search filtered - reset the latestValidLocation
    useEffect(() => {
        latestValidLocationRef.current = null;
    }, [employeeId]);

    // set employee equipment, icon and role
    useEffect(() => {
        if (!props?.employee) return;

        // set employee equipment
        if (props?.employee?.employeeEquipment) {
            setEmployeeEquipment(props?.employee?.employeeEquipment);
        }
        else {
            setEmployeeEquipment(false);
        }

        // set employee icon and role
        if (props?.employee?.role) {
            const found = timelineRoles.find(r => r?.value === props?.employee?.role);

            // Check if driver with van equipment - show van icon instead
            const isDriver = props?.employee?.role === 'driver';
            const hasVanEquipment = props?.employee?.employeeEquipment?.toLowerCase()?.includes('van');

            if (isDriver && hasVanEquipment) {
                setEmployeeIcon(<FaShuttleVan />);
            } else {
                setEmployeeIcon(found?.icon ?? <FaUser />); // fallback default icon
            }

            setEmployeeRole(found?.label ?? props?.employee?.role ?? "")

        } else {
            setEmployeeIcon(<FaUser />)
            setEmployeeRole("Not Selected")
        }
        //old accounts don't have role property, need to demonstrate that.
    }, [props?.employee]);

    // handle shift times - 24 hour format
    useEffect(() => {
        if (props?.shift) {
            setShiftStart(props.shift[0]);
            setShiftEnd(props.shift[1]);
        } else {
            setShiftStart("");
            setShiftEnd("");
        }
    }, [props?.shift])

    // set employee location based on latest beacon and airport locations
    const employeeCurrentLocation = useMemo(() => {

        // get latest data of beacon and geo
        const latestBeacon = empBeaconById?.latestBeacon;
        const latestGeo = empLocationById?.latest;

        if (!Array.isArray(airportLocations)) return null;

        const beaconTimeStr = latestBeacon?.latestBeaconTime;
        const geoTimeStr = latestGeo?.timestamp;

        // fresh beacon always wins — show it even if GPS is more recent
        const beaconMs = toEpochMs(beaconTimeStr);
        if (Number.isFinite(beaconMs) && (Date.now() - beaconMs) <= BEACON_PRIORITY_WINDOW_MS) {
            const freshBeaconResult = findBeaconLoc(latestBeacon, beaconTimeStr);
            return freshBeaconResult;
        }

        // determine latest source out of beacon and geo
        const { source } = latestSource({ beaconTime: beaconTimeStr, geoTime: geoTimeStr });

        // beacon
        if (source == 'beacon') {
            return findBeaconLoc(latestBeacon, beaconTimeStr);
        }

        // geo
        if (source == 'geo') {
            return findGeoLoc(latestGeo, geoTimeStr);
        }

        return null;

    }, [airportLocations, empBeaconById?.latestBeacon, empLocationById?.latest, geoIndex]);

    // Keep track of the last non-null resolved location.
    // When resolution returns null (beacon unregistered, employee outside all geofences, signal gap),
    // show this stale-but-honest fix rather than dropping to "Unknown".
    if (employeeCurrentLocation != null) {
        latestValidLocationRef.current = {
            ...employeeCurrentLocation,
            employeeId,
        };
    }
    const displayLocation = employeeCurrentLocation ??
        (latestValidLocationRef.current?.employeeId === employeeId
            ?
            latestValidLocationRef.current
            :
            null);
    //#endregion

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                    padding: Spacing.SCALE_4,
                    alignItems: 'center',
                    boxSizing: 'border-box',
                }}
                tabIndex="0"
            >

                {/* profile pic circle + status (smaller on phones, where the staff column is narrow) */}
                <div style={{ flex: IS_PHONE_TIMELINE ? '0 0 auto' : 1 }}>
                    <div style={{ borderRadius: '50%', width: avatarSize, height: avatarSize, position: 'relative' }}>

                        {/* employee icon */}
                        <div style={{
                            width: avatarSize, height: avatarSize, borderRadius: 50,
                            backgroundColor: Colors.PRM_LIGHT_BLUE,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        >
                            {/* profile picture or initials */}
                            <span style={{ fontSize: IS_PHONE_TIMELINE ? 15 : 20, color: "#fff", justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                                {employeeIcon}
                            </span>
                        </div>


                        {/* div for status */}
                        <div style={{
                            display: 'flex', justifyContent: 'flex-end', marginTop: -Spacing.SCALE_8,
                            width: avatarSize,
                        }}>
                            <div
                                data-tippy-content={statusMeta.label}
                                style={{
                                    width: 15,
                                    height: 15,
                                    borderRadius: 50,
                                    backgroundColor: statusMeta.color,
                                    borderStyle: 'solid',
                                    borderWidth: 1.5,
                                    borderColor: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: statusMeta.iconColor || 'white',
                                    fontSize: employeeStatus === "onBreak" ? 8 : 9,
                                    boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                                }}
                            >
                                {statusMeta.icon}
                            </div>

                        </div>

                    </div>
                </div>

                {/* employee name */}
                <div
                    style={{ flex: 3, marginLeft: IS_PHONE_TIMELINE ? Spacing.SCALE_4 : Spacing.SCALE_8, minWidth: 0 }}
                    onMouseEnter={(e) => {
                        clearTimeout(infoTooltipTimer.current);
                        const rect = e.currentTarget.getBoundingClientRect();
                        setInfoTooltip({ x: rect.right, y: rect.top + rect.height / 2 });
                    }}
                    onMouseLeave={() => {
                        infoTooltipTimer.current = setTimeout(() => setInfoTooltip(null), 200);
                    }}
                >
                    {props?.showName &&
                        <div style={{ alignItems: "left", color: props?.glow ? 'black' : 'grey', padding: 0, minWidth: 0 }}>

                            {/* employee name */}
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: props?.glow ? "bold" : "500",
                                    textAlign: "left",
                                    margin: 0,
                                    padding: 0,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {props?.employee?.title ?? ""}
                            </div>

                            {/* equipment & shift times */}
                            {(employeeEquipment || (shiftStart && shiftEnd)) && (
                                <div style={{ display: 'flex', margin: 0, padding: 0, flexDirection: 'column' }}>
                                    {employeeEquipment && <span style={{ fontSize: 10.5, color: 'lightgrey' }}>{employeeEquipment}</span>}
                                    {shiftStart && shiftEnd && <span style={{ fontSize: 10.5, color: 'grey' }}>{shiftStart} - {shiftEnd}</span>}
                                </div>
                            )}

                            {/* current location */}
                            {SHOW_EMPLOYEE_LOCATION && <div key={props.employee.employeeId} style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 10,
                                padding: '1px 5px',
                                background: '#f0f0f0',
                                borderRadius: 10,
                                color: '#333',
                                fontWeight: 'bold',
                                border: '1px solid #ccc',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                cursor: 'default',
                                userSelect: 'none',
                            }}>
                                <span style={{ whiteSpace: 'nowrap' }}>
                                    {(() => {
                                        const title = displayLocation?.title ?? 'Unknown';
                                        return title.length > 12 ? title.slice(0, 12) + '…' : title;
                                    })()}
                                </span>
                                {displayLocation?.latestEpochMs && (
                                    <span style={{ fontSize: 9, color: '#888', fontWeight: 500, flexShrink: 0 }}>
                                        · {compactTimeAgo(displayLocation.latestEpochMs)}
                                    </span>
                                )}
                            </div>}
                        </div>
                    }
                </div>

            </div>

            {/* employee info tooltip */}
            {infoTooltip && ReactDOM.createPortal(
                <div
                    onMouseEnter={() => clearTimeout(infoTooltipTimer.current)}
                    onMouseLeave={() => setInfoTooltip(null)}
                    style={{
                        position: 'fixed',
                        top: infoTooltip.y,
                        left: infoTooltip.x + 8,
                        transform: 'translateY(-50%)',
                        background: 'white',
                        border: '1px solid lightgray',
                        borderRadius: 8,
                        padding: '10px 14px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 999999999,
                        minWidth: 180,
                        pointerEvents: 'auto',
                    }}
                >
                    <div style={{ fontSize: 12, fontWeight: 'bold', color: '#333', marginBottom: 6, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
                        {props?.employee?.title ?? 'Employee'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, color: '#999', minWidth: 60 }}>Status</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#555', fontWeight: 500 }}>
                                <span style={{
                                    width: 13,
                                    height: 13,
                                    borderRadius: 50,
                                    background: statusMeta.color,
                                    color: statusMeta.iconColor || 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: employeeStatus === "onBreak" ? 7 : 8,
                                }}>
                                    {statusMeta.icon}
                                </span>
                                {statusMeta.label}
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, color: '#999', minWidth: 60 }}>Role</span>
                            <span style={{ fontSize: 11, color: '#555', fontWeight: 500 }}>{employeeRole || 'Not Set'}</span>
                        </div>
                        {employeeEquipment && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 11, color: '#999', minWidth: 60 }}>Equipment</span>
                                <span style={{ fontSize: 11, color: '#555', fontWeight: 500 }}>{employeeEquipment}</span>
                            </div>
                        )}
                        {shiftStart && shiftEnd && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 11, color: '#999', minWidth: 60 }}>Shift</span>
                                <span style={{ fontSize: 11, color: '#555', fontWeight: 500 }}>{shiftStart} - {shiftEnd}</span>
                            </div>
                        )}
                        {SHOW_EMPLOYEE_LOCATION && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, color: '#999', minWidth: 60 }}>Location</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <FaMapMarkerAlt style={{ fontSize: 10, color: displayLocation?.title ? Colors.PRM_LIGHT_BLUE : 'grey' }} />
                                <span style={{ fontSize: 11, color: '#555', fontWeight: 500 }}>{displayLocation?.title ?? 'Unknown'}</span>
                                {displayLocation?.latestTimeAgo && (
                                    <span style={{ fontSize: 10, color: '#999' }}>({displayLocation.latestTimeAgo})</span>
                                )}
                            </div>
                        </div>}
                        {SHOW_EMPLOYEE_LOCATION && displayLocation?.source && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                                <span style={{ fontSize: 11, color: '#999', minWidth: 60 }}>Source</span>
                                <span style={{
                                    fontSize: 10,
                                    padding: '1px 6px',
                                    borderRadius: 10,
                                    background: displayLocation.source === 'beacon' ? '#e8f0fe' : '#e6f4ea',
                                    color: displayLocation.source === 'beacon' ? '#1a73e8' : '#137333',
                                    fontWeight: 600,
                                }}>{displayLocation.source === 'beacon' ? 'Beacon' : 'GPS'}</span>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default EmployeeProfilePicture;
